import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react'
import { api } from '@/util/api'
import { useEventChannel } from '@/hooks/useEventChannel'
import type { EventResource, StreamResource } from '@/types/events'
import { workspaceError } from '@/lib/workspace'

/**
 * An event's workspace: the event and its cameras, loaded over REST and kept live over the event's workspace channel
 * (docs/REALTIME.md): `stream.created`, `stream.updated` and `stream.deleted` for its cameras, `event.updated` for
 * the event. A message, or a host action that completed, supersedes any load that started before it. After the socket
 * drops, the subscription is confirmed again and the workspace loads afresh; nothing polls.
 *
 * `onAccessLost` runs, with the event's name, when this account may no longer open the workspace: the server's
 * `access.revoked` (sent to a manager who was removed or left), or a refused subscription for an event it manages
 * (docs/features/event-managers.md).
 */
export function useEventWorkspace(
  eventId: string,
  { onAccessLost }: { onAccessLost?: (eventName: string) => void } = {},
) {
  const [event, setEventState] = useState<EventResource | null>(null)
  const [streams, setStreams] = useState<StreamResource[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const request = useRef(0)
  const mutations = useRef(0)
  const previewConnected = useRef(false)
  const setPreviewConnected = useCallback((connected: boolean) => {
    previewConnected.current = connected
  }, [])
  const setEvent = useCallback<Dispatch<SetStateAction<EventResource | null>>>(
    (next) => {
      mutations.current++
      setEventState(next)
    },
    [],
  )
  const mergeStream = useCallback((stream: StreamResource) => {
    mutations.current++
    setStreams((previous) =>
      previous.some((item) => item.id === stream.id)
        ? previous.map((item) => (item.id === stream.id ? stream : item))
        : [...previous, stream],
    )
  }, [])
  const removeStream = useCallback((id: string) => {
    mutations.current++
    setStreams((previous) => previous.filter((stream) => stream.id !== id))
  }, [])
  const load = useCallback(
    async (quiet = false, renewToken = false) => {
      const sequence = ++request.current
      const version = mutations.current
      if (!quiet) setLoading(true)
      try {
        const [eventResponse, streamsResponse] = await Promise.all([
          api.get<{ data: EventResource }>(`/events/${eventId}`),
          api.get<{ data: StreamResource[] }>('/streams', {
            event_id: eventId,
          }),
        ])
        // WebSocket updates and completed host actions supersede an older GET snapshot.
        if (sequence !== request.current || version !== mutations.current)
          return
        setEventState((previous) =>
          previous?.id === eventResponse.data.id &&
          previewConnected.current &&
          !renewToken
            ? {
                ...eventResponse.data,
                attributes: {
                  ...eventResponse.data.attributes,
                  livekit_token:
                    previous.attributes.livekit_token ||
                    eventResponse.data.attributes.livekit_token,
                },
              }
            : eventResponse.data,
        )
        setStreams(streamsResponse.data)
        setError(null)
      } catch (failure) {
        if (sequence === request.current && !quiet)
          setError(
            workspaceError(failure, 'Your event couldn’t load. Try again.'),
          )
      } finally {
        if (sequence === request.current) setLoading(false)
      }
    },
    [eventId],
  )
  useEffect(() => {
    void load()
    // Back online, the LiveKit token may have lapsed meanwhile: load a fresh one.
    const online = () => {
      void load(true, true)
    }
    window.addEventListener('online', online)
    return () => {
      request.current++
      window.removeEventListener('online', online)
    }
  }, [load])
  const upsert = useCallback(
    (data: unknown) => {
      const stream = data as StreamResource
      if (stream?.attributes?.event_id === eventId) mergeStream(stream)
      // A camera moved to another event leaves this one.
      else if (stream?.id) removeStream(stream.id)
    },
    [eventId, mergeStream, removeStream],
  )
  // The event as another organiser or the server changed it. Its LiveKit token is minted for each person who loads the
  // event, and `access` (owner or manager) and `can_manage_managers` are each person's own, so the message carries none
  // of them: the workspace keeps its own.
  const mergeEvent = useCallback(
    (data: unknown) => {
      const next = data as EventResource
      setEvent((previous) =>
        previous && previous.id === next?.id
          ? {
              ...next,
              attributes: {
                ...next.attributes,
                livekit_token: previous.attributes.livekit_token,
                access: previous.attributes.access,
                can_manage_managers: previous.attributes.can_manage_managers,
              },
            }
          : previous,
      )
    },
    [setEvent],
  )
  useEventChannel(eventId, {
    scope: 'workspace',
    on: {
      'stream.created': upsert,
      'stream.updated': upsert,
      'stream.deleted': (data) => removeStream((data as { id: string }).id),
      'event.updated': mergeEvent,
      'access.revoked': () => onAccessLost?.(event?.attributes.name ?? 'this event'),
    },
    onResync: () => void load(true),
    // An organization's own members aren't refused; a manager is, once removed.
    onReject: () => {
      if (event?.attributes.access === 'manager') onAccessLost?.(event.attributes.name)
    },
  })
  const config = useMemo(
    () =>
      event
        ? {
            eventId: event.id,
            token: event.attributes.livekit_token,
            serverUrl: event.attributes.livekit_url,
            roomName: event.attributes.livekit_room_name,
          }
        : null,
    [
      event?.id,
      event?.attributes.livekit_token,
      event?.attributes.livekit_url,
      event?.attributes.livekit_room_name,
    ],
  )
  return {
    event,
    streams,
    loading,
    error,
    config,
    load,
    mergeStream,
    removeStream,
    setEvent,
    setPreviewConnected,
  }
}
